import type { Transition, Variants } from "framer-motion";

/** Shared easing curves — mirror the CSS tokens in styles.css. */
export const EASE = {
  outExpo: [0.16, 1, 0.3, 1] as const,
  spring: [0.34, 1.56, 0.64, 1] as const,
  inOutSoft: [0.65, 0, 0.35, 1] as const,
};

export const SPRING: Record<"soft" | "snappy" | "bouncy", Transition> = {
  soft: { type: "spring", stiffness: 180, damping: 24 },
  snappy: { type: "spring", stiffness: 320, damping: 28 },
  bouncy: { type: "spring", stiffness: 260, damping: 14 },
};

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE.outExpo } },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.35 } },
};

export const popIn: Variants = {
  hidden: { opacity: 0, scale: 0.86 },
  show: { opacity: 1, scale: 1, transition: SPRING.bouncy },
};

/** Stagger container — pair with `fadeUp` children. */
export function stagger(delayChildren = 0, staggerChildren = 0.06): Variants {
  return {
    hidden: {},
    show: { transition: { delayChildren, staggerChildren } },
  };
}

/** Deterministic pseudo-random in [0,1) so particle layouts stay stable per render. */
export function seeded(index: number, salt = 1): number {
  const x = Math.sin((index + 1) * 12.9898 * salt) * 43758.5453;
  return x - Math.floor(x);
}

/** Map a tier/rarity name onto the shared rarity CSS class. */
export function rarityClass(name: string | null | undefined): string {
  const n = (name ?? "").toLowerCase();
  if (/(myth|grail|chase|god)/.test(n)) return "rarity-mythic";
  if (/(legend|gold|ultra|secret)/.test(n)) return "rarity-legendary";
  if (/(epic|purple|super|hit)/.test(n)) return "rarity-epic";
  if (/(rare|blue|holo)/.test(n)) return "rarity-rare";
  return "rarity-common";
}

/** How loud the reveal ceremony should be for a given tier. */
export function rarityIntensity(name: string | null | undefined): 0 | 1 | 2 | 3 {
  switch (rarityClass(name)) {
    case "rarity-mythic":
      return 3;
    case "rarity-legendary":
      return 3;
    case "rarity-epic":
      return 2;
    case "rarity-rare":
      return 1;
    default:
      return 0;
  }
}
